import { z } from 'zod';
import { VollWalletCardStateSchema } from './voll_wallet_card.js';
import { VollWalletRefSchema } from './voll_wallet_pix.js';

/**
 * The card over the pay-bff `PAY_*UserCard*` operations, which serve Stark and Pomelo cards
 * (`/voll-wallet/user-card*`). It follows the legacy app (voll_modules `packages/pay`): the
 * `pay_user_card_component` flag picks this flow; without it the screen stays on the wallets-api
 * one (`/voll-wallet/card*`).
 */
export const VOLL_WALLET_USER_CARD_FEATURE_FLAGS = ['pay_user_card_component'] as const;

/** `GET /voll-wallet/user-card/configuration` — where the card charges. `null` until the traveler
 *  saves one: the legacy app opens "Dados corporativos" before anything else. */
export const VollWalletUserCardConfigurationSchema = z
  .object({
    configuration: z
      .object({
        company: VollWalletRefSchema,
        costCenter: VollWalletRefSchema,
        /** `null` when the client does not use projects (the pay-bff stores it empty). */
        project: VollWalletRefSchema.nullable(),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type VollWalletUserCardConfiguration = z.infer<typeof VollWalletUserCardConfigurationSchema>;

export const VollWalletUserCardBrandSchema = z.enum(['visa', 'mastercard']);
export type VollWalletUserCardBrand = z.infer<typeof VollWalletUserCardBrandSchema>;

/** The issued card, without its sensitive data. */
export const VollWalletUserCardSchema = z
  .object({
    state: VollWalletCardStateSchema,
    last4: z.string(),
    holder: z.string(),
    /** `null` = a brand outside the enum: the face shows the generic logo. */
    brand: VollWalletUserCardBrandSchema.nullable(),
    virtual: z.boolean(),
    /** A card without a PIN opens the PIN creation right away. */
    hasPin: z.boolean(),
    provider: z.enum(['stark', 'pomelo']),
    /** The card at the provider; the digital wallet asks the app to add the card by it (Stark). */
    providerId: z.string(),
  })
  .strict();
export type VollWalletUserCard = z.infer<typeof VollWalletUserCardSchema>;

/** The pay-bff message, already translated; `null` = the screen's default copy. */
const message = z.string().nullable();

/**
 * `POST /voll-wallet/user-card/request` — get-or-issue: the first call asks the provider for the
 * card, the next ones answer where the issuing stands. The screen calls it only after the
 * traveler asks for the card (onboarding), then again to poll while `creating`.
 */
export const VollWalletUserCardStatusSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ready'), card: VollWalletUserCardSchema }).strict(),
  z.object({ status: z.literal('creating') }).strict(),
  z.object({ status: z.literal('under_analysis'), message }).strict(),
  z.object({ status: z.literal('denied'), message }).strict(),
  z.object({ status: z.literal('failed'), message }).strict(),
  z
    .object({
      status: z.literal('missing_fields'),
      message,
      /** `code` is open-ended (`BIRTHDATE`, `PHONE`…); `label` is already translated. */
      fields: z.array(z.object({ code: z.string(), label: z.string() }).strict()),
    })
    .strict(),
]);
export type VollWalletUserCardStatus = z.infer<typeof VollWalletUserCardStatusSchema>;

/**
 * `POST /voll-wallet/user-card/reveal` — Stark answers the data in clear; Pomelo answers a page of
 * its own that shows it (PAN and CVV never leave Pomelo), valid until `expiresAt`. The BFF never
 * logs either and answers `no-store`.
 */
export const VollWalletUserCardDataSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('data'),
      number: z.string().regex(/^\d{12,19}$/),
      /** "MM/AA". */
      expiration: z.string(),
      cvv: z.string().regex(/^\d{3,4}$/),
    })
    .strict(),
  z
    .object({
      kind: z.literal('secure_page'),
      // Only `https`: anything else would reveal with an empty face instead of the error toast.
      url: z.url({ protocol: /^https$/ }),
      expiresAt: z.iso.datetime(),
    })
    .strict(),
]);
export type VollWalletUserCardData = z.infer<typeof VollWalletUserCardDataSchema>;

/** The configuration (`PUT …/configuration`, `VollWalletCardAllocationRequestSchema`) and the PIN
 *  (`POST …/pin`, `VollWalletCardPinRequestSchema`) reuse the wallets-api flow requests. */
export const VollWalletUserCardErrorSchema = z.discriminatedUnion('error', [
  /** The traveler is not on the `pay_user_card_component` flag: use `/voll-wallet/card*`. */
  z.object({ error: z.literal('user_card_not_enabled') }).strict(),
  z.object({ error: z.literal('allocation_invalid') }).strict(),
  z.object({ error: z.literal('pin_invalid') }).strict(),
]);
export type VollWalletUserCardError = z.infer<typeof VollWalletUserCardErrorSchema>;
